import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { accentPresets, type AccentPreset, type DensityMode, type SurfaceVariant } from '@vitness/tokens';

export type ResolvedThemeMode = 'dark' | 'light';
export type ThemeMode = ResolvedThemeMode | 'system';

interface StoredThemeSettings {
  mode: ThemeMode;
  accentName: AccentPreset;
  density: DensityMode;
  surface: SurfaceVariant;
  accentIcons: boolean;
}

export const THEME_STORAGE_KEY = 'vudget-theme';

/**
 * The theme is also mirrored to a domain-scoped cookie so the selection is
 * shared across the VITNESS apps, which live on different origins (localStorage
 * is per-origin and would not sync them). Cookies ignore the port, so dev apps
 * on `localhost:5173/5174/5175` share one value; in prod the cookie is scoped to
 * the registrable domain so subdomains (app./spec./…) share it too.
 */
// Vudget-specific so preferences don't bleed across the other apps on
// this domain — the cookie is scoped to the registrable domain, which is
// exactly why the shared key synced them.
const THEME_COOKIE_KEY = 'vudget-theme';

/** Cookie domain for cross-subdomain sharing; omitted on localhost / IP hosts. */
function themeCookieDomain(): string | undefined {
  const host = window.location.hostname;
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return undefined;
  const parts = host.split('.');
  return parts.length <= 2 ? `.${host}` : `.${parts.slice(-2).join('.')}`;
}

function readThemeCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${THEME_COOKIE_KEY}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeThemeCookie(value: string): void {
  if (typeof document === 'undefined') return;
  const domain = themeCookieDomain();
  document.cookie =
    `${THEME_COOKIE_KEY}=${encodeURIComponent(value)};path=/;max-age=31536000;samesite=lax` +
    (domain ? `;domain=${domain}` : '');
}

const DEFAULT_THEME_SETTINGS: StoredThemeSettings = {
  mode: 'dark',
  accentName: 'green',
  // Upstream's tight scale, unchanged. Overall size is handled by the interface
  // scale (see lib/usePreferences.ts) rather than by density, so the two stay
  // independent: scale makes everything bigger, density controls how much air
  // sits between things.
  density: 'tight',
  surface: 'outlined',
  accentIcons: false,
};

const THEME_MODES: ThemeMode[] = ['dark', 'light', 'system'];
const ACCENT_NAMES = Object.keys(accentPresets) as AccentPreset[];
const DENSITIES: DensityMode[] = ['tight', 'comfortable'];
const SURFACES: SurfaceVariant[] = ['flat', 'outlined', 'elevated'];

export interface ThemeContextValue {
  /** Resolved color scheme currently rendered by the application. */
  mode: ResolvedThemeMode;
  /** Persisted user preference, including operating-system mode. */
  themeMode: ThemeMode;
  accent: string;
  accentSafe: string;
  accentName: AccentPreset;
  accentRgb: { r: number; g: number; b: number };
  density: DensityMode;
  surface: SurfaceVariant;
  accentIcons: boolean;
  setMode: (mode: ThemeMode) => void;
  setAccent: (name: AccentPreset) => void;
  setDensity: (density: DensityMode) => void;
  setSurface: (surface: SurfaceVariant) => void;
  setAccentIcons: (enabled: boolean) => void;
  /** Restores every appearance preference to the shipped defaults. */
  reset: () => void;
}

export function hexToRgb(hex: string) {
  if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) {
    throw new Error('Invalid hex color format');
  }

  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

export function themeSafeAccent(hex: string, isLight: boolean): string {
  if (!isLight) return hex;
  const { r, g, b } = hexToRgb(hex);
  if ((0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55) {
    return `rgb(${Math.floor(r * 0.42)},${Math.floor(g * 0.42)},${Math.floor(b * 0.42)})`;
  }
  return hex;
}

/**
 * Resolves an explicit or system-following theme preference.
 */
export function resolveThemeMode(
  preference: ThemeMode,
  systemMode: ResolvedThemeMode,
): ResolvedThemeMode {
  return preference === 'system' ? systemMode : preference;
}

/**
 * Parses persisted theme settings while rejecting stale or invalid values.
 */
export function parseStoredThemeSettings(value: string | null): StoredThemeSettings {
  if (!value) return DEFAULT_THEME_SETTINGS;

  try {
    const parsed = JSON.parse(value) as Partial<StoredThemeSettings>;
    return {
      mode: THEME_MODES.includes(parsed.mode as ThemeMode)
        ? parsed.mode as ThemeMode
        : DEFAULT_THEME_SETTINGS.mode,
      accentName: ACCENT_NAMES.includes(parsed.accentName as AccentPreset)
        ? parsed.accentName as AccentPreset
        : DEFAULT_THEME_SETTINGS.accentName,
      density: DENSITIES.includes(parsed.density as DensityMode)
        ? parsed.density as DensityMode
        : DEFAULT_THEME_SETTINGS.density,
      surface: SURFACES.includes(parsed.surface as SurfaceVariant)
        ? parsed.surface as SurfaceVariant
        : DEFAULT_THEME_SETTINGS.surface,
      accentIcons: typeof parsed.accentIcons === 'boolean'
        ? parsed.accentIcons
        : DEFAULT_THEME_SETTINGS.accentIcons,
    };
  } catch {
    return DEFAULT_THEME_SETTINGS;
  }
}

function readStoredThemeSettings() {
  if (typeof window === 'undefined') return DEFAULT_THEME_SETTINGS;

  try {
    // The cross-domain cookie wins over per-origin localStorage so the choice
    // made on one app is honoured when landing on another.
    const cookie = readThemeCookie();
    if (cookie) return parseStoredThemeSettings(cookie);
    return parseStoredThemeSettings(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME_SETTINGS;
  }
}

function readSystemTheme(): ResolvedThemeMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }

  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [initialSettings] = useState(readStoredThemeSettings);
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialSettings.mode);
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(readSystemTheme);
  const [accentName, setAccentName] = useState<AccentPreset>(initialSettings.accentName);
  const [densityMode, setDensity] = useState<DensityMode>(initialSettings.density);
  const [surfaceVariant, setSurface] = useState<SurfaceVariant>(initialSettings.surface);
  const [accentIcons, setAccentIconsState] = useState(initialSettings.accentIcons);
  const mode = resolveThemeMode(themeMode, systemMode);

  const accent = accentPresets[accentName];
  const accentRgb = hexToRgb(accent);
  const accentSafe = themeSafeAccent(accent, mode === 'light');

  const setMode = useCallback((preference: ThemeMode) => {
    setThemeMode(preference);
  }, []);

  const setAccent = useCallback((name: AccentPreset) => {
    setAccentName(name);
  }, []);

  const setDensityWrapped = useCallback((d: DensityMode) => {
    setDensity(d);
  }, []);

  const setSurfaceWrapped = useCallback((s: SurfaceVariant) => {
    setSurface(s);
  }, []);

  const setAccentIcons = useCallback((enabled: boolean) => {
    setAccentIconsState(enabled);
  }, []);

  const reset = useCallback(() => {
    setThemeMode(DEFAULT_THEME_SETTINGS.mode);
    setAccentName(DEFAULT_THEME_SETTINGS.accentName);
    setDensity(DEFAULT_THEME_SETTINGS.density);
    setSurface(DEFAULT_THEME_SETTINGS.surface);
    setAccentIconsState(DEFAULT_THEME_SETTINGS.accentIcons);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.setAttribute('data-density', densityMode);
    document.documentElement.style.colorScheme = mode;
  }, [mode, densityMode]);

  /**
   * Fork addition — publish the accent as CSS custom properties.
   *
   * Upstream keeps the accent JS-only: components read `accent`/`accentSafe`
   * off the context and write inline styles. That works for components but
   * leaves stylesheets with no way to reach it, so any `var(--vn-accent)` in
   * plain CSS silently resolves to nothing and the property is dropped —
   * which is how a green brand chip renders as a transparent box.
   *
   * `--vn-accent-rgb` is published as bare channels so stylesheets can build
   * their own alpha: `rgba(var(--vn-accent-rgb), 0.1)`.
   */
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--vn-accent', accent);
    root.setProperty('--vn-accent-safe', accentSafe);
    root.setProperty('--vn-accent-rgb', `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`);
  }, [accent, accentSafe, accentRgb.r, accentRgb.g, accentRgb.b]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const updateSystemMode = (event: MediaQueryListEvent | MediaQueryList) => {
      setSystemMode(event.matches ? 'light' : 'dark');
    };

    updateSystemMode(mediaQuery);
    mediaQuery.addEventListener('change', updateSystemMode);
    return () => mediaQuery.removeEventListener('change', updateSystemMode);
  }, []);

  useEffect(() => {
    const settings: StoredThemeSettings = {
      mode: themeMode,
      accentName,
      density: densityMode,
      surface: surfaceVariant,
      accentIcons,
    };

    const serialized = JSON.stringify(settings);
    // Cookie first so cross-domain sharing works even if localStorage is blocked.
    writeThemeCookie(serialized);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, serialized);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }, [themeMode, accentName, densityMode, surfaceVariant, accentIcons]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return;

      const settings = parseStoredThemeSettings(event.newValue);
      setThemeMode(settings.mode);
      setAccentName(settings.accentName);
      setDensity(settings.density);
      setSurface(settings.surface);
      setAccentIconsState(settings.accentIcons);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Re-read the shared cookie when the tab regains focus, so a theme changed on
  // another app (a different origin, which never fires a `storage` event here)
  // is reflected on return.
  useEffect(() => {
    const sync = () => {
      const cookie = readThemeCookie();
      if (!cookie) return;
      const settings = parseStoredThemeSettings(cookie);
      setThemeMode(settings.mode);
      setAccentName(settings.accentName);
      setDensity(settings.density);
      setSurface(settings.surface);
      setAccentIconsState(settings.accentIcons);
    };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    mode, themeMode, accent, accentSafe, accentName, accentRgb,
    density: densityMode, surface: surfaceVariant, accentIcons,
    setMode, setAccent, setDensity: setDensityWrapped, setSurface: setSurfaceWrapped, setAccentIcons, reset,
  }), [mode, themeMode, accent, accentSafe, accentName, accentRgb, densityMode, surfaceVariant, accentIcons,
    setMode, setAccent, setDensityWrapped, setSurfaceWrapped, setAccentIcons, reset]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
